/**
 * ROUND 15 G2 -- who holds each seat, by name.
 *
 * "Waiting on the Chief Risk Officer" answers why; the reader's
 * question is who to chase. The inbox has named the holder since round
 * 11 F4 from a resolver only it could reach; this reads the same answer
 * for the tolerance panel, the admission block and the pack caption.
 * Names, no email; a seat nobody holds is an empty list, and the
 * sentence says so rather than inventing a person.
 */
import {
  QueryClient,
  QueryClientContext,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useContext } from "react";

import { api, type ApiError } from "shared/api/client";

/* A badge that names a holder sits on surfaces that mount with no
   query client of their own (a test, a stray render); the answer is a
   convenience and must never be the reason a badge throws. */
const FALLBACK_CLIENT = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});

export interface SeatHolders {
  holders: Record<string, string[]>;
}

export const seatHoldersKey = ["seats", "holders"] as const;

export function useSeatHolders(enabled = true): UseQueryResult<SeatHolders, ApiError> {
  const client = useContext(QueryClientContext) ?? FALLBACK_CLIENT;
  return useQuery(
    {
      queryKey: seatHoldersKey,
      queryFn: () => api.get<SeatHolders>("/v1/seats/holders"),
      staleTime: 60_000,
      retry: false,
      enabled,
    },
    client,
  );
}

/** Round 18 G23 -- what to say about who holds a seat: the one person,
 *  "nobody" (the seat is empty), or "unknown" (not loaded, or several
 *  hold it -- naming one of three sends the reader to the wrong one). */
export type SeatHolder =
  { kind: "named"; name: string } | { kind: "nobody" } | { kind: "unknown" };

export function seatHolder(data: SeatHolders | undefined, seat: string): SeatHolder {
  if (!data?.holders) return { kind: "unknown" };
  const who = [...new Set(data.holders[seat] ?? [])];
  if (who.length === 1) return { kind: "named", name: who[0]! };
  if (who.length === 0) return { kind: "nobody" };
  return { kind: "unknown" };
}
